import React, { useMemo } from 'react';
import { useMediaQuery } from '@mui/material';
import Person from '@mui/icons-material/Person';
import Group from '@mui/icons-material/Group';
import Warehouse from '@mui/icons-material/Warehouse';
import Casino from '@mui/icons-material/Casino';
import Settings from '@mui/icons-material/Settings';
import SupportAgent from '@mui/icons-material/SupportAgent';
import LocalOffer from '@mui/icons-material/LocalOffer';
import Payment from '@mui/icons-material/Payment';
import CardGiftcard from '@mui/icons-material/CardGiftcard';
import Storefront from '@mui/icons-material/Storefront';

import Home from '@mui/icons-material/Home';
import MenuBook from '@mui/icons-material/MenuBook';
import Download from '@mui/icons-material/Download';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import Poll from '@mui/icons-material/Poll';
import BugReport from '@mui/icons-material/BugReport';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../config/adminPermissions';
import { isQaRole } from '../../config/qaPermissions';
import UniversalSidebar, { SidebarSection, SidebarItem } from './UniversalSidebar';
import ServerSelection from '../ServerSelection';


interface AppSidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  /** Сайдбар свёрнут на десктопе, чтобы контент занимал всю ширину. */
  collapsed?: boolean;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ mobileOpen, onDrawerToggle, collapsed = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 869px)');

  const isAdmin = isAdminRole(user?.role);
  const isQa = isQaRole(user?.role);

  // Menu items
  const menuItems = useMemo((): SidebarItem[] => [
    { id: 'accounts', text: t('navigation:menu.myAccounts'), icon: <Person />, path: '/dashboard/accounts' },
    { id: 'characters', text: t('navigation:menu.myCharacters'), icon: <Group />, path: '/dashboard/characters' },
    { id: 'warehouse', text: t('navigation:menu.myWarehouse'), icon: <Warehouse />, path: '/dashboard/warehouse' },
    { id: 'shop', text: t('navigation:menu.premiumShop'), icon: <Storefront />, path: '/dashboard/shop', accentColor: '#c49a3c' },

    { id: 'payment', text: t('navigation:menu.payment'), icon: <Payment />, path: '/dashboard/payment', accentColor: '#5a9e7a' },
    { id: 'roulette', text: t('navigation:menu.roulette'), icon: <Casino />, path: '/dashboard/item-wheel', accentColor: '#b05a5a' },
    { id: 'referral', text: t('navigation:menu.referral'), icon: <CardGiftcard />, path: '/dashboard/referrals' },
    { id: 'rating', text: t('navigation:menu.rating'), icon: <EmojiEvents />, path: '/dashboard/ratings' },
    { id: 'polls', text: t('navigation:menu.polls'), icon: <Poll />, path: '/dashboard/polls' },
    { id: 'support', text: t('navigation:menu.support'), icon: <SupportAgent />, path: '/dashboard/support' },
    { id: 'promo', text: t('navigation:menu.promoCodes'), icon: <LocalOffer />, path: '/dashboard/promo-codes' },
    { id: 'settings', text: t('navigation:menu.settings'), icon: <Settings />, path: '/dashboard/settings' },
  ], [t]);

  // Quick links
  const quickLinks = useMemo((): SidebarItem[] => {
    const links: SidebarItem[] = [
      { id: 'home', text: t('navigation:menu.home'), icon: <Home />, path: '/' },
      { id: 'wiki', text: t('navigation:menu.wiki'), icon: <MenuBook />, path: '/wiki' },
      { id: 'download', text: t('navigation:footer.download'), icon: <Download />, path: '/downloads' },
    ];
    if (isAdmin) {
      links.push({ id: 'admin', text: t('navigation:menu.adminPanel'), icon: <AdminPanelSettings />, path: '/admin' });
    }
    // QA-зона есть в шапке, но шапка на мобиле прячет навигацию — без этого пункта
    // в /qa с телефона было не попасть
    if (isQa) {
      links.push({ id: 'qa', text: t('qa:header.panel'), icon: <BugReport />, path: '/qa' });
    }
    return links;
  }, [t, isAdmin, isQa]);

  // Sidebar sections
  const sections = useMemo((): SidebarSection[] => {
    // On mobile, add quick links at the end of menu items
    const allMenuItems = isMobile ? [...menuItems, ...quickLinks] : menuItems;

    return [
      {
        id: 'server-section',
        customContent: <ServerSelection />,
        fixed: true,
      },
      {
        id: 'menu-section',
        withMenuSection: true,
        items: allMenuItems,
      },
    ];
  }, [menuItems, quickLinks, isMobile]);

  return (
    <UniversalSidebar
      sections={sections}
      mobileOpen={mobileOpen}
      onDrawerToggle={onDrawerToggle}
      storageKey="appSidebarExpanded"
      collapsed={collapsed}
    />
  );
};

export default AppSidebar;